import { useCallback, useEffect, useMemo, useState } from "react"

import { isEqual } from "lodash"

import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useCrossTabState } from "@l2r-front/l2r-utils"

import { layersZindexes } from "../../constants/layers"
import { PADDING_BOUNDING_BOX, FIT_TO_BOUNDING_BOX_DURATION, BASE_MAP_ID, MAP_TYPE_ROAD, MAP_TYPE_NETWORK, MAP_TYPE_ALL, MAP_ACTION_CHANGE_STYLE, MAP_TYPE_DETAILS } from "../../constants/map"

import { initialStateContext, MapDispatchContext, MapStateContext } from "./MapContext.context"

export const MapContextProvider = (props) => {
    const { children } = props

    const [mapState, setMapState] = useState(initialStateContext)
    const [selectedImageCollectionType, setSelectedImageCollectionType] = useCrossTabState("selectedImageCollectionType", null)

    const setDisplayReferential = useCallback((displayReferential) => {
        setMapState(previousMapState => ({
            ...previousMapState,
            displayReferential,
        }))
    }, [])

    const setMapRef = useCallback((mapRef, id = BASE_MAP_ID) => {
        setMapState(value => {
            const mapRefs = value.mapRefs || {}
            mapRefs[id] = mapRef

            return ({
                ...value,
                mapRefs: { ...mapRefs },
            })
        })
    }, [setMapState])

    const getMapRef = useCallback((id = BASE_MAP_ID) => {
        return (mapState.mapRefs || {})[id]
    }, [mapState.mapRefs])

    const setMapType = useCallback((mapType) => {
        setMapState(value => ({
            ...value,
            mapType: mapType,
        }))
    }, [setMapState])

    const setMapBoundingBoxToFit = useCallback((mapBoundingBoxToFit, zoom = null, mapId = BASE_MAP_ID) => {
        setMapState(value => ({
            ...value,
            fitOptions: { ...(zoom && { zoom: zoom }), padding: PADDING_BOUNDING_BOX, duration: FIT_TO_BOUNDING_BOX_DURATION, essential: true },
            mapBoundingBoxToFit: {
                ...value.mapBoundingBoxToFit,
                [mapId]: mapBoundingBoxToFit,
            },
        }))
    }, [])

    const storeMapBoundingBox = useCallback((id, mapBoundingBoxToSave) => {
        if (id) {
            if (mapBoundingBoxToSave && isEqual(mapState.storedBoundingBox[id], mapBoundingBoxToSave)) {
                return
            }
            setMapState(value => ({
                ...value,
                storedBoundingBox: {
                    ...value.storedBoundingBox,
                    [id]: mapBoundingBoxToSave,
                },
            }))
            return
        }
        const mapRef = getMapRef()
        if (!mapRef) {
            return
        }

        const mapBoundingBox = mapRef.getBounds()
        const boundingBoxToStore = {
            minLng: mapBoundingBox.getSouthWest().lng,
            minLat: mapBoundingBox.getSouthWest().lat,
            maxLng: mapBoundingBox.getNorthEast().lng,
            maxLat: mapBoundingBox.getNorthEast().lat,
            zoom: mapRef.getZoom(),
        }
        if (mapState.mapType === MAP_TYPE_ROAD && !isEqual(boundingBoxToStore, mapState.storedBoundingBox.road)) {
            setMapState(value => ({
                ...value,
                storedBoundingBox: {
                    ...value.storedBoundingBox,
                    road: boundingBoxToStore,
                },
            }))
        } else if (mapState.mapType === MAP_TYPE_NETWORK && !isEqual(boundingBoxToStore, mapState.storedBoundingBox.network)) {
            setMapState(value => ({
                ...value,
                storedBoundingBox: {
                    ...value.storedBoundingBox,
                    network: boundingBoxToStore,
                },
            }))
        } else {
            setMapState(value => ({
                ...value,
                storedBoundingBox: {
                    ...value.storedBoundingBox,
                    details: boundingBoxToStore,
                },
            }))
        }
    }, [getMapRef, mapState.storedBoundingBox, mapState.mapType, setMapState])

    const resetMapBoundingBox = useCallback((mapType = MAP_TYPE_ALL, id) => {
        setMapState(value => ({
            ...value,
            storedBoundingBox: {
                details: (mapType === MAP_TYPE_DETAILS || mapType === MAP_TYPE_ALL) ? null : value.storedBoundingBox.details,
                road: (mapType === MAP_TYPE_ROAD || mapType === MAP_TYPE_ALL) ? null : value.storedBoundingBox.road,
                network: (mapType === MAP_TYPE_NETWORK || mapType === MAP_TYPE_ALL) ? null : value.storedBoundingBox.network,
                ...(id ? { [id]: null } : {}),
            },
        }))
    }, [setMapState])

    const restoreMapBoundingBox = useCallback((mapType, options = {}, mapId = BASE_MAP_ID) => {
        const bbToRestore = mapType === MAP_TYPE_ROAD ? mapState.storedBoundingBox.road : mapState.storedBoundingBox.network
        if (bbToRestore) {
            setMapState(value => ({
                ...value,
                mapBoundingBoxToFit: {
                    ...value.mapBoundingBoxToFit,
                    [mapId]: bbToRestore,
                },
                fitOptions: {
                    padding: 0,
                    duration: 0,
                    essential: true,
                    ...options,
                },
                storedBoundingBox: {
                    road: mapType === MAP_TYPE_ROAD ? null : value.storedBoundingBox.road,
                    network: mapType === MAP_TYPE_NETWORK ? null : value.storedBoundingBox.network,
                },
            }))
        }
    }, [mapState.storedBoundingBox, setMapState])

    const setMapLayerState = useCallback((action, value, mapId) => {
        if (action === MAP_ACTION_CHANGE_STYLE) {
            setMapState(prevState => ({
                ...prevState,
                mapLayersState: {
                    ...prevState.mapLayersState,
                    [mapId]: {
                        ...prevState.mapLayersState?.[mapId],
                        mapStyle: value,
                    },
                },
            }))
        }
    }, [setMapState])

    const setError = useCallback((error) => {
        setMapState(previousMapState => {
            if (previousMapState.error === error) {
                return previousMapState
            }
            return ({
                ...previousMapState,
                error: error,
            })
        })
    }, [])

    const setSearchRoadCallback = useCallback((callback) => {
        setMapState(previousMapState => ({
            ...previousMapState,
            searchRoadCallback: callback,
        }))
    }, [])

    const toggleLegendOpening = useCallback(() => {
        setMapState(previousMapState => ({
            ...previousMapState,
            legendOpened: !previousMapState.legendOpened,
        }))
    }, [])

    const addLayerClickListener = useCallback((layerId, callback, mapId = BASE_MAP_ID) => {
        setMapState(previousMapState => ({
            ...previousMapState,
            layerClickListeners: {
                ...previousMapState.layerClickListeners,
                [mapId]: {
                    ...previousMapState?.layerClickListeners?.[mapId],
                    [layerId]: callback,
                },
            },
        }))
    }, [])

    const removeLayerClickListener = useCallback((id, mapId = BASE_MAP_ID) => {
        setMapState(previousMapState => {
            const { [id]: _, ...otherListeners } = previousMapState.layerClickListeners[mapId] || {}
            return {
                ...previousMapState,
                layerClickListeners: {
                    ...previousMapState.layerClickListeners,
                    [mapId]: otherListeners,
                },
            }
        })
    }, [])

    const setMapHeightPct = useCallback((height) => {
        setMapState(previousMapState => ({
            ...previousMapState,
            mapHeightPct: height,
        }))
    }, [])

    const setDesktopOpenPhoto = useCallback((desktopOpenPhoto) => {
        setMapState(previousMapState => ({
            ...previousMapState,
            desktopOpenPhoto: desktopOpenPhoto,
        }))
    }, [])


    const setImageLayerDisplayed = useCallback((imageLayerDisplayed) => {
        setMapState(previousMapState => ({
            ...previousMapState,
            imageLayerDisplayed: imageLayerDisplayed,
        }))
    }, [])

    const setMapPin = useCallback((mapId = BASE_MAP_ID, mapPinLocation) => {
        if (mapPinLocation) {
            setMapState(previousMapState => ({
                ...previousMapState,
                mapPin: {
                    [mapId]: mapPinLocation,
                },
            }))
        } else {
            setMapState(previousMapState => {
                const updatedMapPin = { ...previousMapState.mapPin }
                delete updatedMapPin[mapId]
                return ({
                    ...previousMapState,
                    mapPin: updatedMapPin,
                })
            })
        }
    }, [])

    const setUserLocation = useCallback((userLocation) => {
        setMapState(previousMapState => ({
            ...previousMapState,
            userLocation: userLocation,
        }))
    }, [])

    useEffect(() => {
        const mapRefs = Object.keys(mapState.layerClickListeners).map(mapId => getMapRef(mapId))
        const maps = mapRefs.map(mapRef => mapRef?.getMap()).filter(Boolean)
        if (!maps.length) {
            return
        }

        const handleLayersClick = (e) => {
            const map = e.target
            const mapId = map.getContainer().id
            if (!Object.keys(mapState.layerClickListeners[mapId]).length) {
                return
            }
            const features = map.queryRenderedFeatures(e.point)
            const sortedLayerIds = Object.keys(mapState.layerClickListeners[mapId]).sort((layerIdA, layerIdB) => {
                return layersZindexes[layerIdA] < layersZindexes[layerIdB] ? -1 : 1
            })
            for (const layerId of sortedLayerIds) {
                const clickedFeature = features.find(feature => feature.layer.id === layerId)
                if (clickedFeature) {
                    mapState.layerClickListeners[mapId][layerId]({
                        ...e,
                        feature: clickedFeature,
                    })
                    break
                }
            }
        }
        maps.forEach(map => map.on("click", handleLayersClick))
        return function cleanup() {
            maps.forEach(map => map.off("click", handleLayersClick))
        }
    }, [mapState.layerClickListeners, getMapRef])

    const dispatchValue = useMemo(() => {
        return {
            setDesktopOpenPhoto,
            setMapHeightPct,
            setMapType,
            setMapBoundingBoxToFit,
            setMapPin,
            setImageLayerDisplayed,
            setSelectedImageCollectionType,
            storeMapBoundingBox,
            resetMapBoundingBox,
            restoreMapBoundingBox,
            getMapRef,
            setDisplayReferential,
            setMapRef,
            setMapLayerState,
            setError,
            setSearchRoadCallback,
            toggleLegendOpening,
            addLayerClickListener,
            removeLayerClickListener,
            setUserLocation,
        }
    }, [
        setDesktopOpenPhoto,
        setMapPin,
        setImageLayerDisplayed,
        setMapHeightPct,
        setMapType,
        setMapBoundingBoxToFit,
        setSelectedImageCollectionType,
        storeMapBoundingBox,
        resetMapBoundingBox,
        restoreMapBoundingBox,
        getMapRef,
        setDisplayReferential,
        setMapRef,
        setMapLayerState,
        setError,
        setSearchRoadCallback,
        toggleLegendOpening,
        addLayerClickListener,
        removeLayerClickListener,
        setUserLocation,
    ])

    const stateValue = useMemo(() => {
        return ({
            desktopOpenPhoto: mapState.desktopOpenPhoto,
            error: mapState.error,
            fitOptions: mapState.fitOptions,
            displayReferential: mapState.displayReferential,
            getCurrentMapStyle: (mapId = BASE_MAP_ID) => mapState.mapLayersState[mapId]?.mapStyle,
            imageLayerDisplayed: mapState.imageLayerDisplayed,
            mapHeightPct: mapState.mapHeightPct,
            legendOpened: mapState.legendOpened,
            mapBoundingBoxToFit: mapState.mapBoundingBoxToFit,
            mapLayersState: mapState.mapLayersState,
            mapPin: mapState.mapPin,
            mapRef: mapState.mapRef,
            selectedImageCollectionType,
            storedBoundingBox: mapState.storedBoundingBox,
            searchRoadCallback: mapState.searchRoadCallback,
            userLocation: mapState.userLocation,
        })
    }, [mapState, selectedImageCollectionType])

    return (
        <MapStateContext.Provider value={stateValue}>
            <MapDispatchContext.Provider value={dispatchValue}>
                {children}
            </MapDispatchContext.Provider>
        </MapStateContext.Provider>
    )
}

MapContextProvider.propTypes = {
    children: PropTypes.node.isRequired,
}
